import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { LoadingPage } from '@/components/ui/loading';
import { EmptyState } from '@/components/ui/empty-state';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { getProperty } from '../api/properties-api';
import type { Property } from '@/types/api';
import { PropertyScheduledReportsTab } from './property-scheduled-reports-tab';

// Page wrapper for the scheduled-reports list. Lives at
// /properties/:propertyId/scheduled-reports as a sub-route so the table +
// per-row actions get full-page real estate (instead of being squeezed
// into a tab on the property portal).
export function PropertyScheduledReportsPage() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    setLoading(true);
    getProperty(propertyId)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setProperty(res.data);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  if (loading) return <LoadingPage />;
  if (notFound || !property || !propertyId) {
    return (
      <div className="p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <EmptyState
            title="Property not found"
            description="We couldn't load this property. It may have been deleted or you may not have access."
            action={{ label: 'Back to properties', onClick: () => navigate('/properties') }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="space-y-1">
            <Breadcrumb
              items={[
                { label: 'Properties', href: '/properties' },
                { label: property.name, href: `/properties/${property.id}` },
                { label: 'Scheduled Reports' },
              ]}
            />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Scheduled Reports
            </h1>
          </div>
          <Button variant="outline" onClick={() => navigate(`/properties/${property.id}`)}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to property
          </Button>
        </div>

        <PropertyScheduledReportsTab
          propertyId={property.id}
          propertyName={property.name}
          userEmail={user?.email}
        />
      </div>
    </div>
  );
}
